import { Component, Inject, OnInit, AfterViewChecked } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Persona } from 'src/app/models/personas.interface';

export interface MultiSelectReviewDialogData {
  personas: Persona[];
}

export interface MultiSelectReviewDialogResult {
  file: File | null;
  selectedPersonaIds: string[];
}

@Component({
  selector: 'app-multi-select-review-dialog',
  templateUrl: './multi-select-review-dialog.component.html',
  styleUrls: ['./multi-select-review-dialog.component.scss']
})
export class MultiSelectReviewDialogComponent implements OnInit, AfterViewChecked {
  personas: Persona[] = [];
  selectedPersonas: Persona[] = [];
  selectedFile: File | null = null;
  personaSearchControl = new FormControl('');
  filteredPersonas!: Observable<Persona[]>;

  constructor(
    public dialogRef: MatDialogRef<MultiSelectReviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MultiSelectReviewDialogData
  ) {
    this.personas = data.personas || [];
  }

  ngOnInit(): void {
    this.filteredPersonas = this.personaSearchControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterPersonas(value || ''))
    );
  }

  ngAfterViewChecked(): void {
    // Update the Select All checkbox state
    this.updateSelectAllCheckbox();
  }

  private updateSelectAllCheckbox(): void {
    const selectAllOption = document.querySelector('.select-all-option');
    if (selectAllOption) {
      const checkbox = selectAllOption.querySelector('.mat-pseudo-checkbox');
      if (checkbox) {
        // Remove existing state classes
        checkbox.classList.remove('mat-pseudo-checkbox-checked', 'mat-pseudo-checkbox-indeterminate');

        if (this.allSelected) {
          checkbox.classList.add('mat-pseudo-checkbox-checked');
        } else if (this.someSelected) {
          checkbox.classList.add('mat-pseudo-checkbox-indeterminate');
        }
      }
    }
  }

  private _filterPersonas(value: string): Persona[] {
    const filterValue = value.toLowerCase();
    return this.personas.filter(persona => persona.name.toLowerCase().includes(filterValue));
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
    }
  }

  onPersonaSelectionChange(personas: (Persona | string)[]): void {
    // Filter out the special SELECT_ALL value if it somehow gets added
    this.selectedPersonas = personas.filter((p): p is Persona => typeof p !== 'string') as Persona[];
  }

  removePersona(persona: Persona): void {
    this.selectedPersonas = this.selectedPersonas.filter(p => p.id !== persona.id);
  }

  onSubmit(): void {
    const result: MultiSelectReviewDialogResult = {
      file: this.selectedFile,
      selectedPersonaIds: this.selectedPersonas.map(p => p.id).filter((id): id is string => id !== undefined)
    };
    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  get isSubmitDisabled(): boolean {
    return !this.selectedFile || this.selectedPersonas.length === 0;
  }

  get allSelected(): boolean {
    return this.selectedPersonas.length === this.personas.length && this.personas.length > 0;
  }

  get someSelected(): boolean {
    return this.selectedPersonas.length > 0 && this.selectedPersonas.length < this.personas.length;
  }

  toggleSelectAll(): void {
    if (this.allSelected) {
      this.selectedPersonas = [];
    } else {
      this.selectedPersonas = [...this.personas];
    }
  }
}
